import { Repeat } from "@phosphor-icons/react";

const inputCls = "w-full h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm";
const labelCls = "text-xs font-bold uppercase tracking-[0.12em] text-black/60 mb-1 block";

const REPEAT_TYPES = [
  ["daily", "Daily"],
  ["weekday", "Every weekday (Mon–Fri)"],
  ["weekly", "Weekly"],
  ["biweekly", "Every 2 weeks"],
  ["monthly", "Monthly"],
];
const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];

export const defaultRecurrence = () => ({
  enabled: false,
  type: "weekly",
  days: [],
  start_date: new Date().toISOString().slice(0, 10),
  time: "08:00",
  end_mode: "never",
  end_date: "",
  occurrence_limit: 10,
});

// Build the /recurring/series payload from base task fields + recurrence state.
export const buildSeriesPayload = (base, rec) => ({
  title: base.title,
  description: base.description || base.notes || "",
  assigned_employee_id: base.assigned_employee_id || base.assigned_to || null,
  category: base.category,
  priority: base.priority,
  visibility: base.visibility || "private",
  auto_move_rule: base.auto_move_rule || "none",
  recurrence_type: rec.type,
  recurrence_interval: 1,
  recurrence_days: (rec.type === "weekly" || rec.type === "biweekly") ? rec.days : [],
  start_date: rec.start_date || new Date().toISOString().slice(0, 10),
  default_time: rec.time || "08:00",
  end_date: rec.end_mode === "on" ? (rec.end_date || null) : null,
  occurrence_limit: rec.end_mode === "after" ? (parseInt(rec.occurrence_limit) || 1) : null,
  ...(base.voice_capture_id ? { voice_capture_id: base.voice_capture_id } : {}),
});

export default function RecurrenceFields({ value, onChange, testidPrefix = "rec" }) {
  const rec = value;
  const set = (k, v) => onChange({ ...rec, [k]: v });
  const toggleDay = (d) => {
    const has = rec.days.includes(d);
    set("days", has ? rec.days.filter((x) => x !== d) : [...rec.days, d].sort((a, b) => a - b));
  };
  const showDays = rec.type === "weekly" || rec.type === "biweekly";

  return (
    <div className="rounded-lg border-2 border-black bg-[#EEF6FF]">
      <label data-testid={`${testidPrefix}-toggle`} className="flex items-start gap-3 p-3 cursor-pointer">
        <input type="checkbox" className="w-5 h-5 mt-0.5 shrink-0" checked={rec.enabled} onChange={(e) => set("enabled", e.target.checked)} />
        <span>
          <span className="font-bold text-sm flex items-center gap-1"><Repeat size={15} weight="bold" /> Make this recurring</span>
          <span className="text-xs text-black/50">Creates a repeating series that auto-generates each occurrence.</span>
        </span>
      </label>

      {rec.enabled && (
        <div className="px-3 pb-3 space-y-3" onClick={(e) => e.stopPropagation()}>
          <div>
            <label className={labelCls}>Repeat</label>
            <select data-testid={`${testidPrefix}-type`} className={inputCls} value={rec.type} onChange={(e) => set("type", e.target.value)}>
              {REPEAT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          {showDays && (
            <div>
              <label className={labelCls}>Repeat on</label>
              <div data-testid={`${testidPrefix}-days`} className="flex flex-wrap gap-1.5">
                {DAYS.map(([lbl, d]) => (
                  <button key={d} type="button" data-testid={`${testidPrefix}-day-${d}`} onClick={() => toggleDay(d)}
                    className={`press w-10 h-9 rounded-lg border-2 border-black text-xs font-bold ${rec.days.includes(d) ? "bg-primary text-white" : "bg-white"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Starts</label>
              <input data-testid={`${testidPrefix}-start`} type="date" className={inputCls} value={rec.start_date || ""} onChange={(e) => set("start_date", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Time</label>
              <input data-testid={`${testidPrefix}-time`} type="time" className={inputCls} value={rec.time || ""} onChange={(e) => set("time", e.target.value)} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Ends</label>
            <select data-testid={`${testidPrefix}-end-mode`} className={inputCls} value={rec.end_mode} onChange={(e) => set("end_mode", e.target.value)}>
              <option value="never">Never</option>
              <option value="on">On a date</option>
              <option value="after">After N occurrences</option>
            </select>
          </div>
          {rec.end_mode === "on" && (
            <input data-testid={`${testidPrefix}-end-date`} type="date" className={inputCls} value={rec.end_date || ""} onChange={(e) => set("end_date", e.target.value)} />
          )}
          {rec.end_mode === "after" && (
            <input data-testid={`${testidPrefix}-limit`} type="number" min="1" className={inputCls} value={rec.occurrence_limit} onChange={(e) => set("occurrence_limit", e.target.value)} placeholder="Number of occurrences" />
          )}
        </div>
      )}
    </div>
  );
}
